import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Video, Calendar, Clock, MapPin, User, Shield, ExternalLink,
  Loader2, AlertTriangle, CheckCircle2, Globe, Wifi
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const PROVIDER_LABELS = {
  zoom: "Zoom",
  google_meet: "Google Meet",
  glotech_live: "GLOTECH Live Meeting",
  external_link: "Reunion en ligne",
};

export default function EventJoinPage() {
  const { eventId, joinToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verify = async () => {
      try {
        const res = await axios.get(`${API}/event/join/${eventId}/${joinToken}`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Lien d'acces invalide ou expire.");
      } finally {
        setLoading(false);
      }
    };
    verify();
  }, [eventId, joinToken]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 text-[#0099CC] animate-spin mx-auto mb-4" />
          <p className="text-[#94A3B8] text-sm">Verification de votre acces...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-[#151B2B] border border-[#2A3441] rounded-2xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-5">
            <AlertTriangle className="h-8 w-8 text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-3" data-testid="join-error-title">Acces refuse</h1>
          <p className="text-[#94A3B8] text-sm mb-6">{error}</p>
          <Link to="/evenements" className="text-[#0099CC] text-sm hover:underline">Retour aux evenements</Link>
        </div>
      </div>
    );
  }

  const { event, participant, meeting, organization } = data;
  const primaryColor = organization?.primary_color || "#0099CC";
  const providerLabel = PROVIDER_LABELS[meeting?.provider] || "Reunion en ligne";

  const handleJoin = () => {
    if (meeting?.meeting_url) {
      window.open(meeting.meeting_url, "_blank");
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-4 py-10">
      <div className="max-w-lg w-full" data-testid="event-join-page">
        {/* Header with org branding */}
        <div className="text-center mb-8">
          {organization?.logo_url && (
            <img src={organization.logo_url} alt={organization.name} className="h-14 w-14 rounded-full object-cover mx-auto mb-3 border-2" style={{ borderColor: primaryColor }} />
          )}
          <p className="text-[#94A3B8] text-sm">{organization?.name}</p>
        </div>

        {/* Access granted card */}
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl overflow-hidden">
          <div className="h-1.5 w-full" style={{ background: `linear-gradient(to right, ${primaryColor}, #10B981)` }} />
          
          <div className="p-6 sm:p-8">
            {/* Success badge */}
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-full bg-[#10B981]/10 flex items-center justify-center">
                <CheckCircle2 className="h-4 w-4 text-[#10B981]" />
              </div>
              <span className="text-[#10B981] text-sm font-semibold" data-testid="join-access-granted">Acces autorise</span>
            </div>

            {/* Participant greeting */}
            <h1 className="text-2xl font-bold text-white mb-1" data-testid="join-event-title">{event?.title}</h1>
            <p className="text-[#94A3B8] text-sm mb-6">
              Bienvenue, <strong className="text-white">{participant?.first_name} {participant?.last_name}</strong>
            </p>

            {/* Event details */}
            <div className="space-y-2.5 mb-6">
              {event?.event_date && (
                <div className="flex items-center gap-2.5 text-sm text-[#94A3B8]">
                  <Calendar className="h-4 w-4 text-[#64748b]" />
                  {new Date(event.event_date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </div>
              )}
              {(event?.start_time || event?.end_time) && (
                <div className="flex items-center gap-2.5 text-sm text-[#94A3B8]">
                  <Clock className="h-4 w-4 text-[#64748b]" />
                  {event.start_time}{event.end_time ? ` — ${event.end_time}` : ""}
                </div>
              )}
              <div className="flex items-center gap-2.5 text-sm text-[#94A3B8]">
                {event?.event_format === "hybride" ? <Globe className="h-4 w-4 text-[#8B5CF6]" /> : <Wifi className="h-4 w-4 text-[#3B82F6]" />}
                {event?.event_format === "hybride" ? "Evenement hybride" : "Evenement en ligne"}
              </div>
              <div className="flex items-center gap-2.5 text-sm text-[#94A3B8]">
                <User className="h-4 w-4 text-[#64748b]" />
                Ref: <span className="font-mono text-[#0099CC]">{participant?.reference}</span>
              </div>
            </div>

            {/* Meeting access */}
            {meeting?.meeting_url ? (
              <div className="bg-[#0B1120] border border-[#1E3A5F] rounded-xl p-5 mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Video className="h-4 w-4 text-[#3B82F6]" />
                  <span className="text-sm font-semibold text-[#3B82F6]">{providerLabel}</span>
                </div>
                {meeting.waiting_room && (
                  <p className="text-xs text-[#F59E0B] mb-3 flex items-center gap-1.5">
                    <Clock className="h-3 w-3" />
                    Salle d'attente activee — vous serez admis par l'organisateur
                  </p>
                )}
                <Button data-testid="join-meeting-btn" onClick={handleJoin}
                  className="w-full py-3 text-white font-bold rounded-xl shadow-lg transition-all hover:-translate-y-0.5"
                  style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)` }}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Rejoindre la conference
                </Button>
              </div>
            ) : (
              <div className="bg-[#0B1120] border border-[#2A3441] rounded-xl p-5 mb-6 text-center">
                <Clock className="h-8 w-8 text-[#F59E0B] mx-auto mb-2" />
                <p className="text-sm text-[#94A3B8]">Le lien de la reunion sera disponible prochainement.</p>
              </div>
            )}

            {/* Security notice */}
            <div className="flex items-start gap-2.5 text-xs text-[#64748b]">
              <Shield className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <p>Ce lien est personnel et securise. Il est associe a votre inscription et ne doit pas etre partage.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
